import { createEffect, createMemo, createSignal, type JSX, Show } from "solid-js";

import { openWithdrawPrompt, tradeIn } from "~/js/casino";
import { game } from "~/js/game";

import { getCurrency } from "~/js/prefs";
import { addStat } from "~/js/stats";
import { createPage } from "~/js/utils";
import { goToPreviousPage, numToString } from "~/script";
import { user } from "~/userStore";

createPage(() => <MinesPage />, "mines");
const multiplierFactor = 0.992;

function MineElement(props: {
	index: number;
	gameStatus: () => "ended" | "started";
	clearfunction: (index: number) => void;
	loseGame: () => void;
	bombCount: () => number;
	remainingTiles: () => number;
}) {
	const [alreadyClicked, setAlreadyClicked] = createSignal(false);
	const [state, setState] = createSignal<"default" | "bomb" | "cleared">("default");

	createEffect(() => {
		if (props.gameStatus() === "started") {
			setAlreadyClicked(false);
			setState("default");
		}
	});

	return (
		<div
			class="mines-tile"
			classList={{ "mines-bomb": state() === "bomb", "mines-cleared": state() === "cleared" }}
			onclick={() => {
				if (alreadyClicked()) return;
				if (props.gameStatus() === "started") {
					setAlreadyClicked(true);
					const rand = Math.random();

					if (props.bombCount() / props.remainingTiles() >= rand) {
						setState("bomb");
						props.loseGame();
					} else {
						setState("cleared");
						props.clearfunction(props.index);
					}
				}
			}}
			style={{
				top: `${Math.floor(props.index / 5) * 100}px`,
				left: `${(props.index % 5) * 100}px`,
				"background-image": `url(/images/bombsites/${props.index < 12 ? props.index + 1 : props.index}.png)`,
			}}
		></div>
	);
}

function MinesPage() {
	const [betAmount, setBetAmount] = createSignal(10);
	const [bombCount, setBombCount] = createSignal(1);
	const [gameStatus, setGameStatus] = createSignal<"ended" | "started">("ended");
	const [hasPlayedRound, setHasPlayedRound] = createSignal(false);
	const [removedTiles, setRemovedTiles] = createSignal(1);
	const [remainingTiles, setRemainingTiles] = createSignal(24);
	const multiplier = createMemo(() => Math.max((1 / removedTiles()) * multiplierFactor, 1));
	const [lastClearedIndex, setLastClearedIndex] = createSignal<number | null>(null);
	const [multiplierTransitioning, setMultiplierTransitioning] = createSignal(false);

	const multiplierPosition = createMemo(() => {
		const index = lastClearedIndex();
		if (index === null) return { top: "200px", left: "200px" };
		return {
			top: `${Math.floor(index / 5) * 100}px`,
			left: `${(index % 5) * 100}px`,
		};
	});

	const multiplierFontSize = createMemo(() => `${40 - (24 - remainingTiles()) * 1.5}px`);

	function startGame() {
		if (betAmount() <= 0) return;
		if (betAmount() > user.tokens) return;
		user.tokens -= betAmount();
		addStat("spend_chips", betAmount());
		if (bombCount() > 23) setBombCount(23);
		else if (bombCount() < 1) setBombCount(1);
		setRemainingTiles(24);
		setRemovedTiles(1);
		setLastClearedIndex(null);
		setMultiplierTransitioning(false);

		setGameStatus("started");

		game.emit("update");
	}

	function loseGame() {
		setGameStatus("ended");
		if (betAmount() > Math.floor(user.tokens)) setBetAmount(Math.floor(user.tokens));
		setHasPlayedRound(true);
		addStat("total_mines_games", 1);
		addStat("total_mines_lost", 1);
	}

	function winGame() {
		setGameStatus("ended");
		setHasPlayedRound(true);
		user.tokens += Math.floor(betAmount() * multiplier());
		addStat("win_chips", Math.floor(betAmount() * multiplier()));
		if (multiplier() > 20) addStat("total_mines_won_mo20", 1);
		addStat("total_mines_games", 1);
		addStat("total_mines_won", 1);
		game.emit("update");
	}

	function clearTile(index: number) {
		const newRemovedTiles = removedTiles() * ((remainingTiles() - bombCount()) / remainingTiles());
		setRemovedTiles(newRemovedTiles);
		setRemainingTiles((t) => t - 1);
		setLastClearedIndex(index);

		setMultiplierTransitioning(false);
		setTimeout(() => {
			setMultiplierTransitioning(true);
		}, 100);

		if (remainingTiles() - 1 === bombCount()) winGame();
	}
	const handlePlayClick: JSX.EventHandlerUnion<HTMLButtonElement, MouseEvent> = () => {
		if (gameStatus() === "ended") startGame();
		else if (removedTiles() !== 1) winGame();
	};

	const playButtonText = () => {
		// can be inlined
		if (gameStatus() === "started") {
			if (remainingTiles() !== 24) return `Cash out ${Math.floor(betAmount() * multiplier())} C.`;
			return "Press a tile...";
		}
		return hasPlayedRound() ? "Play again" : "Play";
	};

	return (
		<div class="center">
			<h1>Mines</h1>
			<div class="coins">You have {numToString(user.tokens)} chips.</div>
			<div>
				<button class="button" onClick={() => goToPreviousPage()} type="button">
					Back
				</button>
				<button class="button" onClick={() => void tradeIn()} type="button">
					Trade in Skins to Chips
				</button>
				<button class="button" onClick={() => openWithdrawPrompt()} type="button">
					Withdraw Chips to {getCurrency()}
				</button>
			</div>
			<div class="mines-body">
				<div class="mines-left-column">
					<p class="mines-description">
						In Mines, your objective is to uncover as many tiles as possible, without choosing a bomb. If you choose a bomb your bet is
						lost. The more tiles you uncover, the higher your bet multiplier gets. You can cash out at any time before hitting a bomb. Your
						multiplier grows faster if you have more bombs on the board.
					</p>
					<p class="label">Enter the amount you want to bet</p>
					<div>
						{/* controls div */}
						<input
							class="input column-margin"
							max={user.tokens}
							min="1"
							onBlur={(e) => {
								e.currentTarget.value = betAmount().toString(); // do i need this
							}}
							onInput={(e) => {
								if (gameStatus() === "ended") {
									let amount = parseInt(e.currentTarget.value, 10) || 0;
									if (amount > Math.floor(user.tokens)) amount = Math.floor(user.tokens);
									if (user.upgrades.maxbet !== -1 && amount > Math.floor((user.upgrades.maxbet / 100) * 8))
										amount = Math.floor((user.upgrades.maxbet / 100) * 8);
									setBetAmount(amount);
								}
							}}
							type="number"
							value={betAmount()}
						/>
						<button
							class="button combo-button"
							onClick={() => {
								if (gameStatus() === "ended") {
									setBetAmount(Math.floor(user.tokens));
								}
							}}
							type="button"
						>
							All-in
						</button>
					</div>
					<p class="label">Enter the amount of bombs you want</p>
					<div class="select-combo column-margin">
						<button
							class="button"
							classList={{ selected: bombCount() === 1 }}
							disabled={gameStatus() === "started"}
							onClick={() => setBombCount(1)}
							type="button"
						>
							1
						</button>
						<button
							class="button"
							classList={{ selected: bombCount() === 3 }}
							disabled={gameStatus() === "started"}
							onClick={() => setBombCount(3)}
							type="button"
						>
							3
						</button>
						<button
							class="button"
							classList={{ selected: bombCount() === 5 }}
							disabled={gameStatus() === "started"}
							onClick={() => setBombCount(5)}
							type="button"
						>
							5
						</button>
						<input
							class="input hide-number-input-arrows"
							classList={{ selected: ![1, 3, 5].includes(bombCount()) }}
							inputMode="numeric"
							max="23"
							min="1"
							onBlur={(e) => {
								setBombCount(Math.max(Math.min(parseInt(e.currentTarget.value, 10) || 1, 23), 1));
								e.currentTarget.value = bombCount().toString();
							}}
							placeholder="Custom Amount"
							style={{ width: "30px" }}
							type="number"
						/>
					</div>
					<button class="button column-margin" onClick={handlePlayClick} type="button">
						{playButtonText()}
					</button>
				</div>
				<div class="mines-tiles-container" classList={{ "mines-game-ended": gameStatus() === "ended" }}>
					<Show fallback={<div class="mines-multiplier">1.00x</div>} when={gameStatus() === "started" || hasPlayedRound()}>
						{(() => {
							return Array.from({ length: 25 }, (_, i) => i)
								.filter((i) => i !== 12)
								.map((index) => (
									<MineElement
										bombCount={bombCount}
										clearfunction={clearTile}
										gameStatus={gameStatus}
										index={index}
										loseGame={loseGame}
										remainingTiles={remainingTiles}
									/>
								));
						})()}
						<div
							class="mines-multiplier"
							style={{
								top: multiplierPosition().top,
								left: multiplierPosition().left,
								"font-size": multiplierTransitioning() ? multiplierFontSize() : "0px",
								transition: multiplierTransitioning() ? "0.1s font-size" : "0s",
							}}
						>
							{multiplier().toFixed(2)}x
						</div>
					</Show>
				</div>
			</div>
		</div>
	);
}
